import { useGetLabels } from '@rabbit/bizproc/react';
import { CreateTable } from '@rabbit/sage/utils/CreateTable';
import { useTranslation } from 'react-i18next';
import { ModalAddEditItemKind } from '../../organisms/ModalNewEditProduct/ModalAddEditItem';
import { DTLabel } from '@rabbit/data/types';

import English from '../../../../../../libs/mixmaster/locales/en/translation.json';
import {
  Button,
  ButtonIcon,
  LoadingSpinner,
} from '@rabbit/elements/shared-components';
import { PencilSquareIcon } from '@heroicons/react/24/solid';
import { useRef, useState } from 'react';
import { useIdentity } from '@rabbit/data/portal';
import { toast } from 'react-toastify';
import {
  ArrowUturnLeftIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';

export default function LabelsTab({
  handleEditModal,
}: {
  handleEditModal: (
    kind: ModalAddEditItemKind,
    title: string,
    itemDetails: any
  ) => void;
}) {
  const { t, i18n } = useTranslation();
  const [editLabel, setEditLabel] = useState({ key: '', label: '' });
  const inputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(false);
  const { upsertLabel, deleteLabel } = useGetLabels({
    tenantLink: t('tenantLink'),
  });
  const searchRef = useRef<HTMLInputElement>(null);
  const [searchValue, setSearchValue] = useState('');
  const {
    data: { identity },
  } = useIdentity();

  function flattenData(
    obj: any,
    prefix: string = '',
    res: { key: string; label: string }[] = []
  ) {
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const newKey = prefix ? `${prefix}.${key}` : key;
        const value = obj[key];
        if (
          value !== null &&
          typeof value === 'object' &&
          !Array.isArray(value)
        ) {
          flattenData(value, newKey, res);
        } else {
          res.push({ key: newKey, label: value === null ? '' : value });
        }
      }
    }
    return res;
  }

  async function handleSave(data: DTLabel) {
    setLoading(true);
    try {
      await upsertLabel({
        key: data.key,
        label: inputRef.current?.value || data.label,
        tenantLink: t('tenantLink'),
        createdBy: identity?.displayname ?? '',
      });
      setLoading(false);
      setEditLabel({
        key: '',
        label: '',
      });
      toast.success('Saved');
      await i18n.reloadResources();
      await i18n.changeLanguage(i18n.language);
    } catch (e) {
      console.log(e);
      setLoading(false);
      toast.error('Unable to save label');
    }
  }

  async function handleReset(data: DTLabel) {
    setLoading(true);
    try {
      await deleteLabel(data.docid);
      setLoading(false);
      setEditLabel({
        key: '',
        label: '',
      });
      toast.success('Reset to default');
      await i18n.reloadResources();
      await i18n.changeLanguage(i18n.language);
    } catch (e) {
      console.log(e);
      setLoading(false);
      toast.error('Unable to save label');
    }
  }

  const { labelList } = useGetLabels({ tenantLink: t('tenantLink') });

  const allData = flattenData(English) as DTLabel[];
  labelList.data?.forEach((label) => {
    const find = allData.find((data) => data.key === label.key);
    if (find) {
      find.label = label.label;
      find.createdBy = label.createdBy;
      find.docid = label.docid;
    }
  });
  const LabelTable = CreateTable<DTLabel>(
    allData.filter((data) => {
      if (!searchValue) return true;
      return (
        data.key.toLowerCase().match(searchValue.toLowerCase()) ||
        data.label.toLowerCase().match(searchValue.toLowerCase())
      );
    }),
    [
      {
        header: 'Key',
        value: (data) => data.key,
        size: 400,
      },
      {
        header: 'Label',
        size: 400,
        value: (data) =>
          editLabel.key === data.key ? (
            <input
              ref={inputRef}
              className="font-nunito focus:border-primary-600 w-full rounded-lg border-[1px] border-gray-300 bg-gray-50 px-4 py-3 text-base text-gray-900 placeholder-gray-400 !shadow-none !outline-none !ring-0"
              type="text"
              name={'label'}
              defaultValue={data.label}
            />
          ) : (
            data.label
          ),
      },
      {
        header: '',
        accessorKey: 'actions',
        size: 15,
        value: (data) => (
          <div className="w-[280px]">
            {editLabel.key === data.key ? (
              <div className="flex justify-end gap-4 px-4">
                <Button
                  kind="red"
                  onClick={() => setEditLabel({ key: '', label: '' })}
                  size="sm"
                >
                  Cancel
                </Button>
                <Button
                  kind="primary"
                  onClick={() => handleSave(data)}
                  size="sm"
                >
                  Save
                </Button>
                {data.createdBy && (
                  <Button
                    kind="outline"
                    onClick={() => handleReset(data)}
                    size="sm"
                  >
                    <ArrowUturnLeftIcon className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-end gap-4">
                <ButtonIcon
                  label={t('general.editItem')}
                  kind={'bgLightGreen'}
                  iconLeft={true}
                  Icon={PencilSquareIcon}
                  onClick={() => setEditLabel(data)}
                  className="max-w-[120px]"
                />
                {data.createdBy && (
                  <div className="group relative">
                    <InformationCircleIcon className="text-primary-800 h-6 w-6" />
                    <div className="text-primary-800 absolute top-1/2 right-full hidden w-max -translate-y-1/2 rounded-md border border-gray-200 bg-white p-2 text-sm shadow-2xl group-hover:block">
                      Created by: {data.createdBy}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ),
      },
    ]
  );

  return (
    <div className="flex w-full flex-col">
      {loading && <LoadingSpinner size="xs" overlay={true} />}
      <LabelTable
        initialState={{
          showGlobalFilter: true,
          globalFilter: searchValue || '',
          sorting: [{ id: 'key', desc: true }],
        }}
        manualFiltering={true}
        onGlobalFilterChange={(v) => {
          setSearchValue(v);
        }}
        muiSearchTextFieldProps={{
          placeholder: t('general.search'),
          ref: searchRef,
        }}
      />
    </div>
  );
}
